import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import type { CourseProfile } from '../../studio/types/courseProfile';
import { getSubjectById } from '../../studio/constants/subjectCatalog';
import { buildCourseDashboardPath } from '../../studio/constants/courseStudioRoutes';
import { LEARNING_STYLE_OPTIONS, resolveCourseDisplayName } from '../../studio/constants/courseProfileUi';

interface CourseCardProps {
  course: CourseProfile;
}

function learningStyleLabel(id: CourseProfile['learningStyle'][number]): string {
  return LEARNING_STYLE_OPTIONS.find((option) => option.id === id)?.label ?? id;
}

export function CourseCard({ course }: CourseCardProps) {
  const subject = getSubjectById(course.subjectId);
  const title = resolveCourseDisplayName(course);
  const subtitle = subject ? `${subject.name}` : course.subjectTag;

  return (
    <Link
      to={buildCourseDashboardPath(course.id)}
      className="subject-card group relative overflow-hidden"
      style={
        {
          '--subject-hue': subject?.hue ?? 'var(--sala-accent)',
          '--subject-tint': subject?.tint ?? 'var(--sala-surface-raised)',
        } as React.CSSProperties
      }
    >
      <span className="subject-card__accent" aria-hidden />
      <span className="subject-card__body">
        <span className="subject-card__name">
          {title}
          <span className="ml-1 font-normal text-[var(--sala-ink-muted)]">— {subtitle}</span>
        </span>
        <span className="subject-card__desc">
          {course.schoolName?.trim() ? course.schoolName.trim() : null}
          {course.schoolName?.trim() && course.interests.length > 0 ? ' · ' : null}
          {course.interests.length > 0 ? course.interests.slice(0, 2).join(', ') : null}
          {!course.schoolName?.trim() && course.interests.length === 0 ? subject?.name ?? '' : null}
        </span>
        {(course.learningStyle.length > 0 || course.interests.length > 0) && (
          <span className="mt-2 flex flex-wrap gap-1.5">
            {course.learningStyle.slice(0, 2).map((style) => (
              <span
                key={style}
                className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                style={{
                  background: 'color-mix(in srgb, var(--sala-accent) 14%, transparent)',
                  border: '1px solid var(--sala-border-strong)',
                }}
              >
                {learningStyleLabel(style)}
              </span>
            ))}
          </span>
        )}
      </span>
      <span className="subject-card__cta inline-flex items-center gap-1">
        Abrir curso
        <ChevronRight className="h-3.5 w-3.5" />
      </span>
    </Link>
  );
}
